import * as React from 'react';
import { cn } from '../../helpers/shared';

const AutoExpandingTextarea = React.forwardRef(
  ({ className, minRows = 1, maxRows = 10, ...props }, ref) => {
    const textareaRef = React.useRef(null);
    const [rows, setRows] = React.useState(minRows);

    // Combine refs
    React.useImperativeHandle(ref, () => textareaRef.current);

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || textarea.value.trim() === '') {
        setRows(minRows);
        if (textarea) {
          textarea.style.height = 'auto';
        }
        return;
      }
      //if (!textarea) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';

      // Calculate the number of rows based on content
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight) || 20; // fallback to 20px
      const paddingTop = parseInt(computedStyle.paddingTop) || 0;
      const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;

      const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom;
      const calculatedRows = Math.max(1, Math.ceil(contentHeight / lineHeight));

      // Constrain rows between min and max
      const newRows = Math.max(minRows, Math.min(maxRows, calculatedRows));
      setRows(newRows);

      // Set the height explicitly
      const newHeight = newRows * lineHeight + paddingTop + paddingBottom;
      textarea.style.height = `${newHeight}px`;
    }, [minRows, maxRows]);

    // Adjust height when value changes
    React.useEffect(() => {
      adjustHeight();
    }, [props.value, adjustHeight]);

    // Handle window resize
    React.useEffect(() => {
      const handleResize = () => adjustHeight();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [adjustHeight]);

    // Adjust height on input
    const handleInput = (e) => {
      adjustHeight();
      if (props.onInput) {
        props.onInput(e);
      }
    };

    // Handle onChange
    const handleChange = (e) => {
      if (props.onChange) {
        props.onChange(e);
      }
      // Trigger adjustment after state update
      setTimeout(adjustHeight, 0);
    };

    return (
      <textarea
        ref={textareaRef}
        className={cn(
          'ring-offset-background file:text-foreground flex w-[95%] rounded-md border border-gray-300 bg-white px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#7F7F7F] focus:ring-1 focus:ring-blue-300 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none overflow-hidden',
          className,
        )}
        rows={rows}
        onInput={handleInput}
        onChange={handleChange}
        {...props}
      />
    );
  }
);

AutoExpandingTextarea.displayName = 'AutoExpandingTextarea';

export { AutoExpandingTextarea };
